import * as React from "react";
import { Section } from "./TermsContent";

const LAST_UPDATED_ISO = "2025-07-01";

export const CookiesContent: React.FC<{ withPageTitle?: boolean }> = ({
  withPageTitle = true,
}) => {
  const lastUpdated = new Date(LAST_UPDATED_ISO).toLocaleDateString("pt-BR");

  return (
    <div>
      {withPageTitle && (
        <>
          <h1 className="text-3xl font-bold mb-4">
            Política de Cookies da Nacional GO
          </h1>
          <p className="text-sm text-n-text-neutral mb-8">
            Última atualização: {lastUpdated}
          </p>
        </>
      )}

      <Section title="1. O que são Cookies?">
        <p>
          Cookies são pequenos arquivos de texto armazenados no seu navegador
          ou dispositivo quando você acessa um site. Eles permitem que o site
          reconheça seu dispositivo em visitas futuras, lembre preferências e
          melhore sua experiência de navegação.
        </p>
        <p>
          Além dos cookies, também utilizamos tecnologias similares como
          pixels, web beacons e armazenamento local (localStorage) para
          finalidades semelhantes descritas nesta Política.
        </p>
      </Section>

      <Section title="2. Tipos de Cookies que Utilizamos">
        <p>
          <strong>2.1 Cookies Estritamente Necessários</strong>
        </p>
        <p>
          Essenciais para o funcionamento da plataforma. Sem eles, serviços
          solicitados por você não podem ser fornecidos. Não requerem
          consentimento.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Manutenção de sessão autenticada;</li>
          <li>Segurança e prevenção a fraudes;</li>
          <li>Armazenamento de preferências de acessibilidade;</li>
          <li>Balanceamento de carga dos servidores.</li>
        </ul>

        <p className="mt-4">
          <strong>2.2 Cookies de Desempenho e Analytics</strong>
        </p>
        <p>
          Coletam informações sobre como os visitantes usam o site (páginas
          visitadas, tempo de permanência, erros encontrados). Os dados são
          agregados e anônimos.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Google Analytics: métricas de tráfego e comportamento;</li>
          <li>Hotjar: gravações de sessão e mapas de calor (heatmaps);</li>
          <li>Monitoramento de erros e desempenho técnico.</li>
        </ul>

        <p className="mt-4">
          <strong>2.3 Cookies Funcionais</strong>
        </p>
        <p>
          Permitem que o site lembre escolhas feitas por você para oferecer
          uma experiência mais personalizada.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Preferências de idioma e região;</li>
          <li>Dados de formulários preenchidos parcialmente;</li>
          <li>Preferências de notificações.</li>
        </ul>

        <p className="mt-4">
          <strong>2.4 Cookies de Marketing e Publicidade</strong>
        </p>
        <p>
          Utilizados para exibir anúncios relevantes com base nos seus
          interesses, dentro e fora da plataforma.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Meta Pixel (Facebook/Instagram): remarketing e conversões;</li>
          <li>Google Ads: rastreamento de campanhas;</li>
          <li>LinkedIn Insight Tag: campanhas B2B.</li>
        </ul>
      </Section>

      <Section title="3. Cookies de Terceiros">
        <p>
          Alguns cookies são colocados por serviços de terceiros que aparecem
          em nossas páginas. A Nacional GO não controla esses cookies. Eles
          estão sujeitos às políticas de privacidade dos respectivos
          fornecedores:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Google:{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-n-text-green"
            >
              policies.google.com/privacy
            </a>
          </li>
          <li>
            Meta:{" "}
            <a
              href="https://www.facebook.com/privacy/policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-n-text-green"
            >
              facebook.com/privacy/policy
            </a>
          </li>
          <li>
            LinkedIn:{" "}
            <a
              href="https://www.linkedin.com/legal/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-n-text-green"
            >
              linkedin.com/legal/privacy-policy
            </a>
          </li>
          <li>
            Hotjar:{" "}
            <a
              href="https://www.hotjar.com/legal/policies/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-n-text-green"
            >
              hotjar.com/legal/policies/privacy
            </a>
          </li>
        </ul>
      </Section>

      <Section title="4. Duração dos Cookies">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Cookies de sessão:</strong> expiram quando você fecha o
            navegador.
          </li>
          <li>
            <strong>Cookies persistentes:</strong> permanecem no seu
            dispositivo por um período determinado (geralmente de 30 dias a 2
            anos), dependendo da finalidade.
          </li>
        </ul>
      </Section>

      <Section title="5. Gerenciamento e Controle de Cookies">
        <p>
          Você tem o direito de aceitar, recusar ou revogar o consentimento
          para cookies não essenciais a qualquer momento, através das seguintes
          formas:
        </p>
        <p>
          <strong>5.1 Banner de Cookies</strong>
        </p>
        <p>
          Ao acessar nossa plataforma pela primeira vez, exibimos um aviso de
          cookies onde você pode selecionar quais categorias aceita.
        </p>
        <p>
          <strong>5.2 Configurações do Navegador</strong>
        </p>
        <p>
          Você pode configurar seu navegador para bloquear ou excluir cookies:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Chrome:</strong> Configurações → Privacidade e segurança →
            Cookies e outros dados do site.
          </li>
          <li>
            <strong>Firefox:</strong> Configurações → Privacidade e segurança →
            Cookies e dados do site.
          </li>
          <li>
            <strong>Safari:</strong> Preferências → Privacidade → Gerenciar
            dados do site.
          </li>
          <li>
            <strong>Edge:</strong> Configurações → Privacidade, pesquisa e
            serviços → Cookies.
          </li>
        </ul>
        <p>
          Atenção: bloquear cookies essenciais pode impedir o funcionamento
          correto de funcionalidades da plataforma.
        </p>
        <p>
          <strong>5.3 Opt-out de Ferramentas Específicas</strong>
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Google Analytics:{" "}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-n-text-green"
            >
              tools.google.com/dlpage/gaoptout
            </a>
          </li>
          <li>
            Hotjar:{" "}
            <a
              href="https://www.hotjar.com/legal/compliance/opt-out"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-n-text-green"
            >
              hotjar.com/legal/compliance/opt-out
            </a>
          </li>
        </ul>
      </Section>

      <Section title="6. Base Legal">
        <p>
          O uso de cookies pela Nacional GO é fundamentado nas seguintes bases
          legais (LGPD, art. 7º):
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Consentimento</strong> (art. 7º, I): cookies de analytics,
            funcionais e de marketing.
          </li>
          <li>
            <strong>Legítimo interesse</strong> (art. 7º, IX): cookies
            estritamente necessários para segurança e funcionamento da
            plataforma.
          </li>
        </ul>
      </Section>

      <Section title="7. Relação com a Política de Privacidade">
        <p>
          Esta Política de Cookies complementa nossa{" "}
          <a href="/privacidade" className="underline hover:text-n-text-green">
            Política de Privacidade
          </a>
          , que descreve de forma mais ampla como tratamos seus dados pessoais.
          Recomendamos a leitura conjunta dos documentos.
        </p>
      </Section>

      <Section title="8. Atualizações desta Política">
        <p>
          Esta Política de Cookies pode ser atualizada periodicamente para
          refletir mudanças nas tecnologias utilizadas ou na legislação
          aplicável. A versão vigente estará sempre disponível nesta página,
          com a data da última revisão.
        </p>
      </Section>

      <Section title="9. Contato">
        <p>
          Para dúvidas sobre o uso de cookies ou sobre esta Política, entre em
          contato com nosso Encarregado de Proteção de Dados:
        </p>
        <ul className="list-disc pl-5">
          <li>
            E-mail:{" "}
            <a
              href="mailto:privacidade@nacionalgo.com.br"
              className="underline hover:text-n-text-green"
            >
              privacidade@nacionalgo.com.br
            </a>
          </li>
        </ul>
      </Section>
    </div>
  );
};
